/**
 * The commission SPLIT: what share of a commission the advisor keeps.
 *
 * Pure functions, no DOM, no Firebase. Lifted out of `commission.js` on
 * 19 Aug 2026 when a trip gained a split of its own and that file went past
 * the 400-line limit. Two jobs were sitting in one file: working out what a
 * trip's commission IS (still there) and working out how it divides (here).
 *
 * `commission.js` re-exports everything below, so nothing that already
 * imported from there has to change.
 *
 * ── The legacy bugs this half answers for ─────────────────────────────────
 *
 *   3. THE SPLIT IS HARDCODED ON THE ONE SCREEN THAT WORKS.
 *      app.html:7090-7091 — `var agentSplitPct = 70; var agencySplitPct = 30;`
 *      `getCommissionSplit()` is never called there. An advisor who sets
 *      60/40 in Settings still sees "Your Share (70%)" on every trip
 *      dashboard, and is told they earned a number they are not owed.
 *
 *   5. THE SHARES DO NOT ADD UP, TWO DIFFERENT WAYS.
 *      `updateCommissionSplitDisplay` (app.html:6987-6988) computes both
 *      shares from their own percentage, so a stored split that does not
 *      total 100 silently loses or invents money. `renderFinancialDashboard`
 *      (app.html:7092-7093) instead rounds the AGENT share to whole dollars
 *      and gives the remainder — cents and all — to the agency. Two
 *      functions, two answers for one split of one number. Here the agent
 *      share is rounded to the nearest CENT and the agency is always the
 *      remainder, so the two always sum to the commission exactly.
 */

import { amount, clampCents, fromCents, toCents } from './money.js'

/** Legacy default, `getCommissionSplit()` (app.html:8333). */
export const DEFAULT_SPLIT = Object.freeze({ agent: 70, agency: 30 })

/** Legacy's own message for a split that does not total 100. app.html:4527. */
export const SPLIT_ERROR = 'Agent and Agency percentages must sum to 100%'

/**
 * A percentage typed into a box, or null if there isn't one.
 *
 * Distinct from `percent` below, which is for reading STORED values and lets
 * fractions through. This one rounds, because the two boxes have to add to
 * exactly 100 and 33.4/66.6 is a pair a person cannot type their way out of.
 *
 * `Number('')` and `Number(null)` are both 0 — a real percentage — so blanks
 * are rejected before the number is taken, not after. An emptied box that read
 * as a typed zero would jump the box beside it to 100.
 *
 * @param {unknown} value
 * @returns {number|null}
 */
export function splitPercent(value) {
  if (value == null) return null
  if (typeof value === 'string' && value.trim() === '') return null
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  return Math.min(100, Math.max(0, Math.round(n)))
}

/**
 * The other half of the split.
 *
 * Legacy's `updateCommSplitAgency` (app.html:8350) and `updateCommSplitAgent`
 * (8357) are the same rule read from either end: typing in one box fills the
 * other so the pair always sums to 100. One function, since it is one rule.
 *
 * A field cleared to empty leaves the other alone rather than jumping it to
 * 100 — legacy's `parseInt('')` is NaN and it writes `100 - NaN`, which puts
 * the literal string "NaN" in the box beside the one being typed into.
 *
 * @param {unknown} typed
 * @returns {number|null} the complement, or null to leave the other alone
 */
export function complementOf(typed) {
  const n = splitPercent(typed)
  return n == null ? null : 100 - n
}

/**
 * Is this split saveable?
 *
 * Legacy `validateCommSplit` (app.html:8364) shows the error line when the two
 * do not sum to 100 — and then `saveSettings` (8377) SILENTLY SKIPS the write.
 * No toast, no abort, the modal closes and the split is unchanged.
 *
 * @param {{agent: unknown, agency: unknown}} draft
 * @returns {boolean}
 */
export function splitBalances(draft) {
  const agent = splitPercent(draft && draft.agent)
  const agency = splitPercent(draft && draft.agency)
  if (agent == null || agency == null) return false
  return agent + agency === 100
}

/**
 * A percentage, clamped to something a split can mean. Never NaN.
 * @param {unknown} value
 * @returns {number|null}
 */
function percent(value) {
  if (value == null || value === '') return null
  const n = amount(value)
  if (!Number.isFinite(n)) return null
  return Math.min(100, Math.max(0, n))
}

/**
 * The advisor's commission split, from whatever shape it was stored in.
 *
 * `mbmstudio_commSplit` holds `'{"agent":70,"agency":30}'` — a STRING, because
 * `setCommissionSplit` JSON-stringifies it (app.html:8337). An object is
 * accepted too, so a caller that has already parsed it does not have to
 * re-serialise. Anything unreadable falls back to 70/30 rather than throwing:
 * legacy's `JSON.parse` (app.html:8332) is unguarded and takes the whole
 * dashboard down with it if the value is ever corrupt.
 *
 * `balanced` is false when the stored pair did not total 100. In that case the
 * AGENT figure wins and the agency becomes the remainder, which is the rule
 * the settings modal itself uses when you type in either box
 * (app.html:8340–8351). A consumer that wants to warn can read the flag; the
 * numbers are always safe to use.
 *
 * @param {unknown} stored
 * @returns {{agent: number, agency: number, balanced: boolean}}
 */
export function commissionSplit(stored) {
  let raw = stored
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw)
    } catch {
      raw = null
    }
  }
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_SPLIT, balanced: true }

  const agent = percent(/** @type {any} */ (raw).agent)
  const agency = percent(/** @type {any} */ (raw).agency)

  if (agent == null && agency == null) return { ...DEFAULT_SPLIT, balanced: true }
  if (agent == null) return { agent: 100 - /** @type {number} */ (agency), agency: /** @type {number} */ (agency), balanced: true }
  if (agency == null) return { agent, agency: 100 - agent, balanced: true }

  const balanced = Math.abs(agent + agency - 100) < 1e-9
  return balanced ? { agent, agency, balanced } : { agent, agency: 100 - agent, balanced: false }
}

/**
 * The split THIS trip is on, falling back to the account's.
 *
 * ── Why a trip carries its own ────────────────────────────────────────────
 *
 * Trey, 19 Aug 2026: "as agents do more business, their commission changes.
 * some trips may fall under one split and then the split might change in the
 * middle of other trips. we need to be able to assign a % split to each trip
 * and have that changable throughout the trip."
 *
 * So the Settings split stopped being THE split and became the DEFAULT — what
 * a new trip starts on. Changing it in Settings no longer restates what every
 * past trip earned, which is exactly what it did before: an advisor moving
 * from 70/30 to 80/20 in March saw January's commission go up.
 *
 * One split per trip, editable at any time — Trey chose that over stamping a
 * split onto each booking. Change it and the whole trip recalculates.
 *
 * A trip with nothing stored, or with something unreadable, takes the
 * fallback — never a hardcoded 70/30. A corrupt value on one trip must not
 * quietly move that trip onto a different split from the rest of the account.
 *
 * @param {Record<string, any>|null|undefined} trip
 * @param {unknown} [fallback] - the account default, from Settings
 * @returns {{agent: number, agency: number, balanced: boolean, own: boolean}}
 */
export function tripSplit(trip, fallback) {
  const stored = trip ? (trip.commSplit ?? (trip.data && trip.data.commSplit)) : null
  const own = readSplit(stored)
  if (own) return { ...own, own: true }
  return { ...commissionSplit(fallback), own: false }
}

/**
 * A split, or null if there is not one in there.
 *
 * `commissionSplit` answers 70/30 for anything it cannot read, which is right
 * for the account default and wrong for a trip: "this trip says nothing" and
 * "this trip says 70/30" have to be told apart, or a trip on the default could
 * never follow a change to the default.
 *
 * @param {unknown} stored
 * @returns {{agent: number, agency: number, balanced: boolean}|null}
 */
function readSplit(stored) {
  if (stored == null || stored === '') return null
  let raw = stored
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw)
    } catch {
      return null
    }
  }
  if (!raw || typeof raw !== 'object') return null
  const agent = percent(/** @type {any} */ (raw).agent)
  const agency = percent(/** @type {any} */ (raw).agency)
  if (agent == null && agency == null) return null
  return commissionSplit(raw)
}

/**
 * Split one commission figure between advisor and agency.
 *
 * The agency share is the REMAINDER, never a second independent rounding, so
 * `agent + agency === total` holds for every input including refunds. See
 * bug 5 above.
 *
 * @param {unknown} total - commission, in dollars or as a money string
 * @param {unknown} [split] - anything `commissionSplit` accepts
 * @returns {{total: number, agent: number, agency: number, agentPct: number, agencyPct: number}}
 */
export function splitCommission(total, split) {
  const pct = commissionSplit(split)
  const totalCents = toCents(total)

  /* Multiply first where it is safe, divide first where it is not: at the
     clamp ceiling `totalCents * 100` would leave the exact-integer range and
     start rounding in units of dollars. */
  const scaled =
    Math.abs(totalCents) <= Number.MAX_SAFE_INTEGER / 100
      ? (totalCents * pct.agent) / 100
      : (totalCents / 100) * pct.agent

  const agentCents = clampCents(scaled)
  const agencyCents = clampCents(totalCents - agentCents)

  return {
    total: fromCents(totalCents),
    agent: fromCents(agentCents),
    agency: fromCents(agencyCents),
    agentPct: pct.agent,
    agencyPct: pct.agency,
  }
}

/**
 * One row of the Commission Tracker, under both of legacy's field schemas.
 *
 * `amount` is the stated `comm{i}_amt` when it is non-zero, and otherwise
 * `gross * rate / 100` — legacy's own recovery (app.html:7084), which is the
 * only reason any commission is visible anywhere today (bug 1).
 *
 * @param {Record<string, any>|null|undefined} data - `trip.data`
 * @param {number} i
 * @returns {{slot: number, company: string, gross: number, rate: number, amount: number, status: string, received: boolean, computed: boolean}}
 */

/* ── typing a split into two boxes ────────────────────────────────────────
   Three screens do it — the Settings modal, the trip editor's Trip Settings
   panel, and both of the create-a-trip flows. The rules live here so those
   cannot disagree, and so a feature never has to import another feature
   (ARCHITECTURE.md section 3). */

/**
 * Typing in one box fills the other — Settings' rule, read from either end
 * (`complementOf` above), so the two panels cannot disagree about what
 * a split is.
 *
 * An emptied box leaves the other alone. Legacy writes `100 - NaN` and puts
 * the string "NaN" in it.
 *
 * @param {{agent: number|string, agency: number|string}} draft
 * @param {'agent'|'agency'} field
 * @param {string} value
 * @returns {{agent: number|string, agency: number|string}}
 */
export function editSplit(draft, field, value) {
  const other = field === 'agent' ? 'agency' : 'agent'
  const next = { ...draft, [field]: value === '' ? '' : Number(value) }
  const complement = complementOf(value)
  return complement == null ? next : { ...next, [other]: complement }
}

/**
 * The split to store, or null to leave what is stored alone.
 *
 * Only a BALANCED pair is written. Trip Settings disables Save on an
 * unbalanced one as well; this is the second check, because a split written
 * half-typed is money changing under the advisor.
 *
 * @param {{agent: number|string, agency: number|string}} draft
 * @returns {{agent: number, agency: number}|null}
 */
export function splitToStore(draft) {
  if (!splitBalances(draft)) return null
  return { agent: Number(draft.agent), agency: Number(draft.agency) }
}

/**
 * A commission split off untrusted input, or null.
 *
 * What `trip-shape.js#newTrip` runs a `commSplit` through before storing it.
 *
 * Stored as two plain numbers rather than the JSON string `/app` uses for the
 * account default: this is a new field with no legacy reader, and a number in
 * Firestore should be a number. A string is still accepted, because the
 * importer and the AI assistant both build trips from loosely-typed input.
 *
 * @param {unknown} value
 * @returns {{agent: number, agency: number}|null}
 */
export function commSplitOf(value) {
  let raw = value
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw)
    } catch {
      return null
    }
  }
  if (!raw || typeof raw !== 'object') return null
  const agent = Number(/** @type {any} */ (raw).agent)
  const agency = Number(/** @type {any} */ (raw).agency)
  if (!Number.isFinite(agent) || !Number.isFinite(agency)) return null
  return { agent, agency }
}
